import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const Cart = ({ showCart, setShowCart, cart, updateCartItem, removeFromCart, openCheckout }) => {
    const { t, getProductName } = useLanguage();
    const total = cart.reduce((sum, item) => sum + Number(item.salePrice || item.price) * item.quantity, 0);

    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.3 } },
    };

    const panelVariants = {
        hidden: { x: '100%' },
        visible: { x: '0%', transition: { duration: 0.3, ease: 'easeInOut' } },
    };

    return (
        <AnimatePresence>
            {showCart && (
                <motion.div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    variants={overlayVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onClick={() => setShowCart(false)}
                >
                    <motion.div 
                        className="fixed right-0 top-0 h-full w-full max-w-sm bg-white/70 backdrop-blur-lg shadow-xl flex flex-col border-l border-white/20" 
                        variants={panelVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b border-black/10">
                            <h2 className="text-xl font-semibold text-gray-800">{t('shoppingCart')}</h2>
                            <button onClick={() => setShowCart(false)} className="p-2 rounded-full text-gray-600 hover:bg-black/10 text-2xl">&times;</button>
                        </div>

                        {cart.length === 0 ? (
                            <div className="flex-grow flex flex-col items-center justify-center text-gray-700 p-8">
                                <svg className="h-16 w-16 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                                <p className="text-lg">{t('cartEmpty')}</p>
                                <button onClick={() => setShowCart(false)} className="mt-4 px-6 py-2 bg-[#b08d57] text-white font-semibold rounded-md hover:bg-[#b08d57]/90">
                                    {t('continueShopping')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex-grow overflow-y-auto p-4 space-y-4">
                                    {cart.map(item => {
                                        const imageUrl = (item.imageUrls && item.imageUrls[0]) || 'https://placehold.co/80x80/cccccc/ffffff?text=No+Image';
                                        const displayName = getProductName(item);
                                        return (
                                        <div key={item.id} className="flex items-start gap-4">
                                            <img loading="lazy" src={imageUrl} alt={displayName} className="w-20 h-20 object-cover rounded-md border border-black/10" />
                                            <div className="flex-grow">
                                                <h3 className="text-md font-medium text-gray-900">{displayName}</h3>
                                                <div className="flex items-center gap-2">
                                                    {item.salePrice ? (
                                                        <>
                                                         <p className="text-sm text-red-600 font-bold">EGP {Number(item.salePrice || 0).toFixed(2)}</p>
                                                         <p className="text-xs text-gray-500 line-through">EGP {Number(item.price || 0).toFixed(2)}</p>
                                                        </>
                                                    ) : (
                                                         <p className="text-sm text-gray-700">EGP {Number(item.price || 0).toFixed(2)}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center mt-2">
                                                    <button onClick={() => updateCartItem(item.id, item.quantity - 1)} className="px-2 py-1 border border-gray-300 rounded-md text-gray-700">-</button>
                                                    <span className="px-3 text-gray-800">{item.quantity}</span>
                                                    <button onClick={() => updateCartItem(item.id, item.quantity + 1)} className="px-2 py-1 border border-gray-300 rounded-md text-gray-700">+</button>
                                                </div>
                                            </div>
                                            <div className="text-end">
                                                <p className="font-semibold text-gray-800">EGP {(Number(item.salePrice || item.price) * item.quantity).toFixed(2)}</p>
                                                <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-700 text-sm mt-2 font-semibold">{t('remove')}</button>
                                            </div>
                                        </div>
                                    );})}
                                </div>
                                <div className="p-4 border-t border-black/10">
                                    <div className="flex justify-between items-center text-lg font-semibold text-gray-800"><span>{t('subtotal')}</span><span>EGP {total.toFixed(2)}</span></div>
                                    <p className="text-sm text-gray-600 mt-1">{t('shippingCalculatedAtCheckout')}</p>
                                   
                                    <button onClick={openCheckout} className="w-full mt-2 py-3 px-4 bg-[#b08d57] text-white font-semibold rounded-md hover:bg-[#b08d57]/90">{t('proceedToCheckout')}</button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Cart;
